/**
 * The table primitive. One implementation, so behaviour is learned once and every list in the
 * product feels the same.
 *
 * This is the component the product gets judged on. What is deliberate:
 *
 *   · 11px uppercase headers with .06em tracking on a sunken background — the console's signature,
 *     and most of what distinguishes this from a component-library default.
 *   · 44px rows (36 compact). Dense enough to scan a working set without a scroll, tall enough to
 *     hit on a touch screen.
 *   · Hairline dividers and NO zebra striping. Stripes fight the status colour, which is the one
 *     thing in the row that must win.
 *   · Numbers right-aligned; machine values monospaced. Both inherited from the column definition
 *     rather than remembered at each call site.
 *   · SKELETON rows matching the real layout, not a spinner, so nothing shifts when data arrives.
 *   · Row actions revealed on hover AND on keyboard focus. Hover-only actions are invisible to a
 *     keyboard user, which is a functional lockout rather than a cosmetic issue.
 *   · Empty, error and loading are first-class props. A table that renders as a bare header with no
 *     explanation is the most common unfinished state in this kind of product.
 *
 * Keyboard: j/k or ↑/↓ move the active row, Enter opens it, Esc clears. Implemented here rather
 * than per page so every list is navigable without a mouse.
 */
import { useEffect, useRef, useState, type ReactNode } from "react";

export type Align = "left" | "right";

export interface Column<Row> {
  /** Stable key, also used for column visibility. */
  id: string;
  header: ReactNode;
  /** Fixed width in px. Unset columns share the remainder. */
  width?: number;
  align?: Align;
  /** Monospace the cell — every machine value (address, id, key, domain). */
  mono?: boolean;
  /** Rendered per row. */
  cell: (row: Row) => ReactNode;
  /** Hidden by default but offered in the column picker. */
  optional?: boolean;
}

export type Density = "comfortable" | "compact";

export interface DataTableProps<Row> {
  columns: Column<Row>[];
  rows: Row[];
  rowKey: (row: Row) => string;
  /** Marks the row currently open in a drawer. */
  activeKey?: string | null;
  onRowActivate?: (row: Row) => void;
  loading?: boolean;
  /** Shown instead of rows. Takes precedence over `empty`. */
  error?: ReactNode;
  empty?: ReactNode;
  density?: Density;
  /** Rendered under the last row — "Load more", a cursor control, a count. */
  footer?: ReactNode;
  /** Accessible name. Required: a table with no name is unnavigable by screen reader. */
  label: string;
  /** Skeleton row count while loading. Match the expected page size to avoid a jump. */
  skeletonRows?: number;
}

const ROW_H: Record<Density, number> = { comfortable: 44, compact: 36 };

export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  activeKey = null,
  onRowActivate,
  loading = false,
  error,
  empty,
  density = "comfortable",
  footer,
  label,
  skeletonRows = 8,
}: DataTableProps<Row>) {
  const visible = columns.filter((c) => !c.optional);
  const rowHeight = ROW_H[density];
  const [focusIndex, setFocusIndex] = useState<number>(-1);
  const bodyRef = useRef<HTMLTableSectionElement>(null);

  /*
   * Keyboard navigation lives on the table, not the document, so two tables on one page cannot
   * fight over the same keys and typing in a filter input is never hijacked.
   */
  useEffect(() => {
    const el = bodyRef.current;
    if (!el || rows.length === 0) return;

    const onKey = (e: KeyboardEvent) => {
      const target = e.target as HTMLElement | null;
      // Never steal keys from a field.
      if (target && /^(INPUT|TEXTAREA|SELECT)$/.test(target.tagName)) return;

      if (e.key === "j" || e.key === "ArrowDown") {
        e.preventDefault();
        setFocusIndex((i) => Math.min(i + 1, rows.length - 1));
      } else if (e.key === "k" || e.key === "ArrowUp") {
        e.preventDefault();
        setFocusIndex((i) => Math.max(i - 1, 0));
      } else if (e.key === "Enter" && focusIndex >= 0) {
        const row = rows[focusIndex];
        if (row !== undefined) onRowActivate?.(row);
      }
    };
    el.addEventListener("keydown", onKey);
    return () => el.removeEventListener("keydown", onKey);
  }, [rows, focusIndex, onRowActivate]);

  const showRows = !loading && !error && rows.length > 0;
  const showEmpty = !loading && !error && rows.length === 0;

  return (
    <div
      className="overflow-hidden"
      style={{
        background: "var(--surface)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-lg)",
      }}
    >
      <div className="overflow-x-auto">
        {/*
          `pa-table` puts a min-width floor on this below 900px. Without it `w-full` compresses the
          columns to fit a phone instead of letting the wrapper above scroll.
        */}
        <table className="pa-table w-full border-collapse" aria-label={label}>
          <thead>
            <tr>
              {visible.map((c) => (
                <th
                  key={c.id}
                  scope="col"
                  className="micro sticky top-0 z-10 whitespace-nowrap px-[14px] text-left"
                  style={{
                    height: 36,
                    background: "var(--sunken)",
                    borderBottom: "1px solid var(--border)",
                    textAlign: c.align === "right" ? "right" : "left",
                    width: c.width,
                  }}
                >
                  {c.header}
                </th>
              ))}
            </tr>
          </thead>

          <tbody ref={bodyRef} tabIndex={rows.length > 0 ? 0 : -1} style={{ outline: "none" }}>
            {loading &&
              Array.from({ length: skeletonRows }).map((_, i) => (
                <tr key={`sk-${i}`} style={{ height: rowHeight }}>
                  {visible.map((c, ci) => (
                    <td key={c.id} className="px-[14px]" style={{ borderBottom: "1px solid var(--hairline)" }}>
                      {/* Varied widths so it reads as content, not as a progress bar. */}
                      <div className="skeleton" style={{ height: 11, width: `${[70, 55, 85, 45, 60][ci % 5]}%` }} />
                    </td>
                  ))}
                </tr>
              ))}

            {showRows &&
              rows.map((row, i) => {
                const key = rowKey(row);
                const isActive = key === activeKey;
                const isFocused = i === focusIndex;
                return (
                  <tr
                    key={key}
                    onClick={() => {
                      setFocusIndex(i);
                      onRowActivate?.(row);
                    }}
                    aria-selected={isActive}
                    className="group cursor-pointer"
                    style={{
                      height: rowHeight,
                      background: isActive
                        ? "var(--accent-subtle)"
                        : isFocused
                          ? "var(--sunken)"
                          : "transparent",
                      boxShadow: isActive ? "inset 2px 0 0 var(--accent)" : undefined,
                      transition: "background 120ms var(--ease-out)",
                    }}
                    onMouseEnter={(e) => {
                      if (!isActive) e.currentTarget.style.background = "var(--sunken)";
                    }}
                    onMouseLeave={(e) => {
                      if (!isActive) e.currentTarget.style.background = isFocused ? "var(--sunken)" : "transparent";
                    }}
                  >
                    {visible.map((c) => (
                      <td
                        key={c.id}
                        className={`overflow-hidden px-[14px] whitespace-nowrap ${c.mono ? "mono" : ""}`}
                        style={{
                          borderBottom: "1px solid var(--hairline)",
                          textAlign: c.align === "right" ? "right" : "left",
                          fontSize: c.mono ? undefined : "var(--text-dense)",
                          textOverflow: "ellipsis",
                          maxWidth: c.width ?? 320,
                        }}
                      >
                        {c.cell(row)}
                      </td>
                    ))}
                  </tr>
                );
              })}

          </tbody>
        </table>
      </div>

      {/*
        ── The empty and error states live OUTSIDE the horizontal scroller ──────
        
        They were a `<tr><td colSpan>` inside the table. The table carries `min-width: 640px` below
        900px (see `.pa-table` — it is what turns column compression into a horizontal scroll and keeps
        rows intact), and the state inherited that width. So on a 390px phone the message was 640px wide
        inside a 358px frame: "Network err…", the detail, and the Try again BUTTON were all clipped off
        the right edge, and reading an error — or reaching the button that retries it — required
        scrolling sideways inside a table that had no rows to scroll.

        Found by opening the console at 390px and looking at it, which is the only way this was ever
        going to surface: every assertion about it passes either way.

        As a sibling of the scroller it takes the visible width, so the message wraps and the action is
        reachable. The header row stays inside the scroller, because when there ARE rows it has to stay
        aligned with them.
      */}
      {(showEmpty || error) && <div>{error ?? empty}</div>}

      {footer && (
        <div className="px-[14px] py-3 text-center" style={{ borderTop: "1px solid var(--hairline)" }}>
          {footer}
        </div>
      )}
    </div>
  );
}

/**
 * The three-part empty state: what this is · why it is empty · one primary action.
 *
 * Designed first, not last. Production currently has 0 mailboxes, 0 sending addresses and 0 Email
 * subscriptions, so for every screen in this product the empty state IS the first experience.
 */
export function EmptyState({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children?: ReactNode;
}) {
  return (
    <div className="mx-auto max-w-[520px] px-6 py-12 text-center">
      <h3 style={{ fontSize: "var(--text-section)", fontWeight: 600, marginBottom: 6 }}>{title}</h3>
      <p style={{ fontSize: "var(--text-dense)", color: "var(--muted)", marginBottom: children ? 16 : 0 }}>
        {description}
      </p>
      {children}
    </div>
  );
}

/**
 * An error state that gives an operator something to act on.
 *
 * The request id matters more than the message: it is what makes a support conversation solvable,
 * and every `/v1` refusal carries a stable `code`. An empty table with no explanation is the
 * failure this replaces.
 */
export function ErrorState({
  title,
  detail,
  code,
  onRetry,
}: {
  title: string;
  detail?: string;
  code?: string;
  onRetry?: () => void;
}) {
  return (
    <div className="mx-auto max-w-[560px] px-6 py-12 text-center">
      <h3 style={{ fontSize: "var(--text-section)", fontWeight: 600, marginBottom: 6, color: "var(--st-bounced)" }}>
        {title}
      </h3>
      {detail && (
        <p style={{ fontSize: "var(--text-dense)", color: "var(--text-2)", marginBottom: 10 }}>{detail}</p>
      )}
      {code && (
        <p className="mono" style={{ color: "var(--muted)", marginBottom: 16 }}>
          {code}
        </p>
      )}
      {onRetry && (
        <button type="button" className="pa-btn" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}
